import React, { useState } from "react";
import { useRequest } from "../../../../hooks/useRequest";
import { Box, Typography, useMediaQuery } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import routes from "../../../../routes";

export type ImageUploadProps = {
  id?: string;
  imageButton?: boolean;
  maxFileSize?: number;
  initialNewImage?: boolean;
  hoverLabel?: string;
  dropLabel?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  borderRadius?: string;
  editPage?: boolean;
  imageUrl?: string;
  imageStyle?: {
    width?: string;
    height?: string;
  };
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>) => void;
};

export const ImageUploader: React.FC<ImageUploadProps> = ({
  id = "",
  imageButton = false,
  maxFileSize = 1024,
  hoverLabel = "Click or drag to upload client picture",
  dropLabel = "Drop image here",
  width = "100%",
  height = "100%",
  editPage = true,
  initialNewImage = false,
  imageUrl = "https://placehold.co/200x200/white/white",
}) => {
  const [imageShownUrl, setImageShownUrl] = React.useState(imageUrl);
  const [labelText, setLabelText] = useState<string>(hoverLabel);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [newImage, setNewImage] = useState<boolean>(initialNewImage);
  const fileLimitDefinition = "2MB";

  const { fetch, response, error, loading } = useRequest();

  const handleImageUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("image", file);
    const setUrl = id
      ? routes.api.client.addImage({ id })
      : routes.api.client.addImageToNewClient({});

    await fetch("POST", setUrl, formData, undefined, false);
    if (response?.data) {
      setImageShownUrl(response?.data);
    }

    return {
      details: response?.data,
      error,
      loading,
    };
  };

  const stopDefaults = (e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const dragEvents = {
    onMouseEnter: () => {
      setIsMouseOver(true);
    },
    onMouseLeave: () => {
      setIsMouseOver(false);
    },
    onDragEnter: (e: React.DragEvent) => {
      stopDefaults(e);
      setIsDragOver(true);
      setLabelText(dropLabel);
    },
    onDragLeave: (e: React.DragEvent) => {
      stopDefaults(e);
      setIsDragOver(false);
      setLabelText(hoverLabel);
    },
    onDragOver: stopDefaults,
    onDrop: async (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer?.files?.[0];
      const fileType = droppedFile.type;

      if (!droppedFile) {
        return;
      }

      if (droppedFile.size > maxFileSize) {
        setErrorMessage(
          `File should be up to ${fileLimitDefinition}, please choose a smaller image`,
        );
        return;
      }
      if (!(fileType === "image/jpeg" || fileType === "image/png")) {
        setErrorMessage(
          "File should be JPEG or PNG format, please choose a different image",
        );
        return;
      }
      try {
        await handleImageUpload(droppedFile);
        setImageShownUrl(URL.createObjectURL(droppedFile));
        setNewImage(true);
        setErrorMessage("");
      } catch (err) {
        setErrorMessage("Failed to upload image. Please try again.");
        console.error(err);
      }
    },
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target && event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target?.files?.[0];
      const fileType = selectedFile.type;

      if (selectedFile.size > maxFileSize) {
        setErrorMessage(
          `File should be up to ${fileLimitDefinition}, please choose a smaller image`,
        );
        return;
      }
      if (!(fileType === "image/jpeg" || fileType === "image/png")) {
        setErrorMessage(
          "File should be JPEG or PNG format, please choose a different image",
        );
        return;
      }
      try {
        await handleImageUpload(selectedFile);
        setImageShownUrl(URL.createObjectURL(selectedFile));
        setNewImage(true);
        setErrorMessage("");
      } catch (err) {
        setErrorMessage("Failed to upload image. Please try again.");
        console.error(err);
      }
    }
  };

  const uploadMessage = !imageButton || isDragOver || isMouseOver;
  const highlightInteraction = isDragOver || isMouseOver;

  const style = {
    message: {
      opacity: errorMessage || highlightInteraction ? 1 : 0.3,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      position: "absolute",
      borderRadius: "5px",
      fontWeight: 900,
    },
    uploadBox: {
      cursor: "pointer",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      width: width,
      height: height,
      position: "absolute",
      borderRadius: "5px",
      backgroundImage: `url(${imageShownUrl})`,
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
    },
  };

  const breakPoint = editPage ? "700px" : "1227px";
  const isMobile = useMediaQuery(`(max-width: ${breakPoint})`);
  const fontSize = isMobile ? "0.5rem" : "1rem";

  return (
    <>
      <input
        type="file"
        accept="image"
        onChange={handleChange}
        id="file-upload"
        aria-label="Upload client picture"
        width="100%"
        height="100%"
        style={{ opacity: 0 }}
      />
      <Box
        data-testid="profile-picture-upload"
        component="label"
        htmlFor="file-upload"
        {...dragEvents}
        sx={{
          ...style.uploadBox,
        }}
      >
        {uploadMessage && !newImage && (
          <Box
            sx={{
              ...style.message,
              backgroundColor: highlightInteraction ? "#70e49c" : "transparent",
            }}
          >
            <AddPhotoAlternateIcon
              sx={{ fontSize: isMobile ? "2rem" : "3rem", padding: "0.5rem" }}
            />
            <Typography
              sx={{
                fontSize,
                padding: "0 1rem 1rem 1rem",
              }}
            >
              {labelText}
            </Typography>
          </Box>
        )}
        {errorMessage && (
          <Typography
            variant="caption"
            sx={{
              ...style.message,
              backgroundColor: "#323438CC",
              color: "white",
              fontSize: fontSize,
              padding: "1rem",
              zIndex: 1,
            }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    </>
  );
};
